import { Review } from './product'

/**
 * The verified-review feed is effectively endless: names, trades, locations and
 * quotes are combined with coprime steps so every page of results is a
 * combination the visitor has not seen yet.
 */
const firstNames = [
  'Chris',
  'Dale',
  'Wyatt',
  'Curtis',
  'Randy',
  'Owen',
  'Vince',
  'Marty',
  'Dustin',
  'Grant',
  'Leon',
  'Terry',
  'Brent',
  'Colby',
  'Rico',
  'Jared',
  'Nolan',
  'Sam',
  'Gus',
  'Manny',
  'Trent',
  'Gabe',
  'Lyle',
  'Cody',
  'Darnell',
  'Pete',
  'Rusty',
  'Emmett',
  'Kurt',
  'Javier',
  'Hank',
  'Devon',
  'Blake',
  'Omar',
  'Clay',
  'Tyrone',
  'Wes',
  'Andre',
  'Jimmy',
  'Sergio',
  'Roy',
  'Bryce',
  'Luis',
  'Damon',
  'Scotty',
  'Reggie',
  'Nate',
  'Casey',
]

const initials = ['A.', 'B.', 'C.', 'D.', 'F.', 'G.', 'H.', 'J.', 'K.', 'L.', 'M.', 'N.', 'P.', 'R.', 'S.', 'T.', 'V.', 'W.']

const trades = [
  'Diesel mechanic',
  'Master electrician',
  'HVAC installer',
  'Plumber',
  'Roofing foreman',
  'Fleet technician',
  'Heavy equipment operator',
  'Millwright',
  'Auto tech',
  'Cabinet maker',
  'Low-voltage tech',
  'Pipefitter',
  'Tow operator',
  'Concrete foreman',
  'Maintenance tech',
  'Welder',
  'Lineman',
  'Farm mechanic',
  'Marine mechanic',
  'Elevator tech',
  'Framing carpenter',
  'Detailer',
  'Warehouse lead',
  'Motorcycle tech',
  'Solar installer',
  'Locksmith',
  'Boiler tech',
  'Small engine tech',
]

const states = [
  'Texas',
  'Ohio',
  'Georgia',
  'Arizona',
  'Michigan',
  'Florida',
  'Nevada',
  'Idaho',
  'Kentucky',
  'Alabama',
  'Oregon',
  'Kansas',
  'Iowa',
  'Montana',
  'Virginia',
  'Indiana',
  'Wisconsin',
  'Louisiana',
  'Oklahoma',
  'Utah',
  'Maine',
  'Minnesota',
  'Arkansas',
  'Nebraska',
  'Wyoming',
  'New Mexico',
  'South Carolina',
  'West Virginia',
  'North Dakota',
  'Mississippi',
]

const quotes = [
  'Did a whole brake job in a dark bay without touching a drop light. That never happens.',
  'The light goes where I look. Sounds simple until you work a full day with it.',
  'Amber lens in dust, smoke lens at noon, clear inside. One frame does all of it.',
  'My headlamp gave me a headache by lunch. These I forget I have on.',
  'Charged it Sunday night with the same cable as my phone. Ready all week.',
  'Crawlspace work used to be the worst part of the job. Now it is just work.',
  'Dropped mine off a ladder onto concrete. They replaced it free, no argument.',
  'Bought one, then bought two more within the month. One for the truck, one for the shop.',
  'Reading breaker labels in a dark panel room is finally a one-person job.',
  'Both hands on the wrench, light on the bolt. That is the whole pitch and it delivers.',
  'Best forty dollars I have spent on gear in ten years.',
  'Got the 3-pack because free is free. Gave one to my dad, he wants his own now.',
  'No strap marks, no sweat band, no shifting around when I look down.',
  'Behind a dashboard at 11pm, the light stayed exactly where I needed it.',
  'Handed one to my apprentice and he stopped borrowing my flashlight.',
  'The lifetime replacement is what made me buy three instead of one.',
  'Working a trailer roof at dusk, switched to clear and kept going another hour.',
  'Lighter than my safety glasses and it puts out more light than my old headlamp.',
  'I stopped looking for a place to set a light down. That alone is worth it.',
  'Six on the crew now. Job photos look better and we finish before dark bites us.',
  'Fixed a well pump at 4am in the rain. Light never moved off the fitting.',
  'Changed out a water heater in a closet with zero lighting. Easy now.',
  'Gave one as a gift and immediately had to order a replacement for myself.',
  'Barn work at night, both hands on the animal or the tool. Game changer.',
  'Lens swap takes seconds. I do it without thinking now.',
  'Twenty-two years in the trade and this is the first gadget that stuck.',
  'Bought the crew pack for the shop. Cheapest morale boost I have ever bought.',
  'The light is bright enough for engine bay work but not blinding up close.',
  'Scratched a lens on day three, they sent a new one. No receipt hunting.',
  'Wearing them under a hood beats holding a light in your mouth every single time.',
  'My back thanks me. No more twisting to aim a light at what I am doing.',
  'Took it up on a service call in an attic in July. Clear lens, no fogging issues.',
  'Fits under a hard hat fine. That was my one worry and it was not a problem.',
  'Loaded out a trailer at 5am before sunrise and never touched a flashlight.',
  'I own three now and I still think about buying a fourth for the camper.',
  'Everything comes in the box. No hunting for accessories after you order.',
]

const bought = ['Bought the 3-pack', 'Bought the crew pack', 'Bought 1, came back for 3', 'Bought the 3-pack']

/** Stable pseudo-random palette so a given monogram always keeps its color. */
const monogramTints = ['#F3C400', '#30343A', '#5b6069', '#8a6d1f', '#3f4650', '#6b7280']

export interface FeedReview extends Review {
  monogram: string
  tint: string
}

export function buildFeedReviews(startIndex: number, count: number): FeedReview[] {
  const items: FeedReview[] = []
  for (let i = startIndex; i < startIndex + count; i += 1) {
    const first = firstNames[i % firstNames.length]
    const initial = initials[(i * 7 + 3) % initials.length]
    const trade = trades[(i * 5 + 1) % trades.length]
    const state = states[(i * 11 + 4) % states.length]
    const quote = quotes[(i * 13 + 2) % quotes.length]
    items.push({
      name: `${first} ${initial}`,
      trade: `${trade} · ${state}`,
      bought: bought[(i * 3) % bought.length],
      quote,
      avatar: '',
      monogram: `${first.charAt(0)}${initial.charAt(0)}`,
      tint: monogramTints[(i * 5) % monogramTints.length],
    })
  }
  return items
}
